import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

// Pad di firma su canvas nativo (nessuna libreria esterna, vedi CLAUDE.md:
// niente dipendenze nuove nel frontend per un flusso legale critico). Un solo
// set di Pointer Events copre mouse, dito e penna. Sfondo canvas sempre
// trasparente: il container bianco (definito dal chiamante, vedi design
// handoff "Firma Contratto - Step") si vede sotto, e il PNG esportato deve
// poter stare sopra la riga di firma nel PDF.
//
// Componente "puro": nessuna label/pulsante proprio — riquadro, guida e riga
// di stato ("Firma acquisita" / "Cancella") sono del chiamante, che decide
// come comporli attorno al canvas.
const COLORE_TRATTO = "#1C1F2E";
const SPESSORE_TRATTO = 2.4;

const PadFirma = forwardRef(function PadFirma({ onCambio, style = {}, className = "" }, ref) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const disegnandoRef = useRef(false);
  const puntoPrecedenteRef = useRef(null); // ultimo punto grezzo ricevuto: control point del prossimo segmento
  const ultimoMedioRef = useRef(null); // punto in cui è terminato l'ultimo segmento disegnato: inizio del prossimo
  const boxRef = useRef(null); // bounding box dei tratti, per capire se "c'è" una firma
  const [haFirma, setHaFirma] = useState(false);

  const dimensiona = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = SPESSORE_TRATTO;
    ctx.strokeStyle = COLORE_TRATTO;
    ctxRef.current = ctx;
  };

  useEffect(() => {
    dimensiona();
    window.addEventListener("resize", dimensiona);
    return () => window.removeEventListener("resize", dimensiona);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segnaCambio = (valore) => {
    setHaFirma(valore);
    onCambio?.(valore);
  };

  const posizione = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const aggiornaBox = (p) => {
    const box = boxRef.current;
    if (!box) {
      boxRef.current = { minX: p.x, maxX: p.x, minY: p.y, maxY: p.y };
      return;
    }
    box.minX = Math.min(box.minX, p.x);
    box.maxX = Math.max(box.maxX, p.x);
    box.minY = Math.min(box.minY, p.y);
    box.maxY = Math.max(box.maxY, p.y);
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    disegnandoRef.current = true;
    const p = posizione(e);
    puntoPrecedenteRef.current = p;
    ultimoMedioRef.current = p;
    aggiornaBox(p);
  };

  // Curva quadratica punto-medio-a-punto-medio: ogni segmento riparte esattamente
  // da dove è finito il precedente (ultimoMedioRef), usando l'ultimo punto grezzo
  // come control point — è quello che rende il tratto continuo invece che
  // spezzato, perché disegnare da ogni punto grezzo al proprio punto medio (come
  // prima) lascia scoperto il tratto fra un punto medio e il successivo punto grezzo.
  const onPointerMove = (e) => {
    if (!disegnandoRef.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const prev = puntoPrecedenteRef.current;
    const curr = posizione(e);
    if (ctx && prev && ultimoMedioRef.current) {
      const medio = { x: (prev.x + curr.x) / 2, y: (prev.y + curr.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(ultimoMedioRef.current.x, ultimoMedioRef.current.y);
      ctx.quadraticCurveTo(prev.x, prev.y, medio.x, medio.y);
      ctx.stroke();
      ultimoMedioRef.current = medio;
    }
    puntoPrecedenteRef.current = curr;
    aggiornaBox(curr);

    const box = boxRef.current;
    if (box && !haFirma && (box.maxX - box.minX > 8 || box.maxY - box.minY > 8)) {
      segnaCambio(true);
    }
  };

  const onPointerUp = () => {
    // Ultimo tratto, dal punto medio finale fino al punto grezzo dov'è
    // stato sollevato il dito/penna: senza questo l'ultima frazione di
    // tratto (fra l'ultimo punto medio e la posizione di rilascio) resta
    // scoperta, evidente sui tratti brevi.
    const ctx = ctxRef.current;
    const ultimo = ultimoMedioRef.current;
    const finale = puntoPrecedenteRef.current;
    if (ctx && ultimo && finale) {
      ctx.beginPath();
      ctx.moveTo(ultimo.x, ultimo.y);
      ctx.lineTo(finale.x, finale.y);
      ctx.stroke();
    }
    disegnandoRef.current = false;
    puntoPrecedenteRef.current = null;
    ultimoMedioRef.current = null;
  };

  const pulisci = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (canvas && ctx) {
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    }
    boxRef.current = null;
    segnaCambio(false);
  };

  const esportaPng = () => {
    if (!haFirma) return null;
    return canvasRef.current.toDataURL("image/png");
  };

  useImperativeHandle(ref, () => ({ esportaPng, pulisci }));

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ display: "block", width: "100%", height: 200, touchAction: "none", cursor: "crosshair", ...style }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="pad-firma-canvas"
    />
  );
});

export default PadFirma;
